<template>
    <div class="container">
        <div v-if="isValidDevice() && !loading" class="row buttondiv">
            <div class="auggie buttons col-6">
                <a-button v-if="allowAssistedCalibration" class="btn btn-info nobounce" @click="onClickedAssist"><img src="/img/AuggieIcon50static.png" alt="Auggie" class="me-4" :disabled="!isValidDevice">Assisted Calibration</a-button>
                <div :class="buttonGap" />
                <a-button v-if="allowAssistedTargetDisplay" class="btn btn-info nobounce" @click="onClickedAssistTargetDisplay"><img src="/img/AuggieIcon50static.png" alt="Auggie" class="me-4" :disabled="!isValidDevice">Target Request</a-button>
                <div :class="buttonGap" />
                <a-button v-if="allowSelfCalibration" class="btn btn-info nobounce" @click="onClickedSelf"><img src="/img/AuggieIcon50static.png" alt="Auggie" class="me-4" :disabled="!isValidDevice">Self Guided Calibration</a-button>
            </div>
            <div class="col-1 "> </div>
            <div class="col-5" v-show="haveUrl && !loading">
                <div class="row">
                    <div class="qrcodearea" v-show="haveUrl">
                        <div class="qrcode">
                            <div v-if="isDemo()" class="qrDemo">DEMO</div>
                            <qrcode :value="url" logo></qrcode>
                        </div>
                        <div class="debugurl" v-show="isDebug">{{ url }}</div>
                    </div>
                    <div class="mt-1">
                        <header-message v-show="haveUrl" class="status-smallest">Please scan this QR code using your mobile device to connect.</header-message>
                    </div>
                </div>
            </div>
        </div>
        <loading :loading="loading" class="mt-5" text="Loading ..." />
        <header-message class="mt-5" v-show="isUnknownDevice()">Establishing server connection...</header-message>
        <header-message class="mt-5" v-show="isInvalidDevice()">This device is not available for calibrations.</header-message>
        <header-message class="mt-5" v-show="isPackageTooNew()">{{ packageTooNewText() }}<br/>{{ packageLatestText() }}</header-message>
        <div class="footer">{{ getVersion() }}{{ getEnvironment() }}</div>
        <power-off />
    </div>
</template>

<style scoped lang="scss">
    .container {
        height:100%;
        position:relative;
    }
    .buttondiv {
        position: relative;
        width: 100%;
        margin-top: 3rem;
        .buttons {
            height: 270px;
            vertical-align: middle;
            display: table-cell;
        }
    }
    a {
        width:100%;
        font-size: .6em;
        padding:.5rem 1rem;
        text-align:left;
        font-weight: bold;
        text-shadow: 3px 6px 7px #555;
        color: darkblue;
        border-width: 15px;
        border-bottom-color: gray;
        border-right-color: darkgray;
    }
    a:hover {
        border-bottom-color: gray;
        border-right-color: darkgray;
    }
    .auggie svg {
        width: 1.5em;
        height: 1em;
    }

    .qrcodearea {
        text-align: center;
    }
    .qrcode {
        margin:auto;
        background: white;
        text-align: center;
        width:270px;
        height:270px;
        // Centering the code with flex keeps the white border even. Relying on the canvas's own margins
        // does not: inline it reserves baseline space below, block its top margin collapses through here.
        display: flex;
        align-items: center;
        justify-content: center;
        position:relative;
        .qrDemo {
            position:absolute;
            font-weight: bold;
            color: orange;
            font-size: 6rem;
            transform: rotate(-35deg);
            top: 4rem;
        }
    }
    .debugurl {
        font-size:.5rem;
    }

    .footer {
        font-size:.75em;
        padding: 1rem 0 1rem 2rem;
        position:fixed;
        bottom:0;
        left:0;
    }

    @media screen and (min-width: 1920px) {
        .buttondiv {
            margin-top: 8.5rem;
        }
        a {
            padding:1rem 1rem;
        }
    }
</style>

<script lang="ts">
import { defineComponent } from "vue";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import { confirmOk, confirmYesNo } from "@/shared/components/common/AlertDialog.vue";
import PageBase from "@/shared/components/common/PageBase";
import { CalibrationType } from "@/shared/enums/CalibrationType";
import { AUGGIEAVAILABLE } from "@/main";
import { CalibrationSessionDto } from "@/shared/models/CalibrationSessionDto";
import { PackageDto } from "@/shared/models/PackageDto";
import { DataRequest } from "@/shared/support/Data";
import { Global } from "@/support/GlobalData";
import { DeviceStatus, SignalR } from "@/support/SignalR";
import HeaderMessage from "@/components/HeaderMessage.vue";
import PowerOff from "@/components/PowerOff.vue";
import Utility from "@/shared/support/Utility";
import Qrcode from "@/components/Qrcode.vue";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { SessionType } from "@/shared/enums/SessionType";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";

export default defineComponent({
    name: "Home",
    components: {
        HeaderMessage,
        PowerOff,
        Qrcode,
    },
    mixins: [PageBase],
    data() {
        return {
            urlCache: "",
            MaxStartSessionAttemps: 10,
            StartSessionInterval: 5 * 1000,
        };
    },
    computed: {
        allowAssistedCalibration(): boolean {
            return Global.AllowAssistedCalibration;
        },
        allowAssistedTargetDisplay(): boolean {
            return Global.AllowAssistedTargetDisplay;
        },
        allowSelfCalibration(): boolean {
            return Global.AllowSelfCalibration;
        },
        buttonGap(): string {
            let count = 0;
            if (this.allowAssistedCalibration) ++count;
            if (this.allowAssistedTargetDisplay) ++count;
            if (this.allowSelfCalibration) ++count;

            switch (count) {
                case 3: return "mt-3";
                case 2: return "mt-5";
                default: return "";
            }
        },
        url: {
            get(): string {
                return this.urlCache;
            },
            set(url: string): void {
                this.urlCache = url;
            }
        },
        haveUrl(): boolean {
            return this.url.length > 0;
        },
        isDebug(): boolean {
            return import.meta.env.VITE_DEBUG === "1";
        },
    },
    created(): void {
        Global.MainHome = this;
    },
    mounted(): void {
        this.killTeamViewer();
        // An accepted device got here without a page reload; register again instead of relying on the earlier registration.
        if (this.isValidDevice() && !Global.IsDemo()) {
            if (Global.DetailLog) console.log("Home: re-registering an accepted device");
            SignalR.sendDeviceStartSession();
        }
        this.startConnection();
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onVehicleSelectionByTechnician = this.setVehicleSelectionByTech;
        SignalR.onAssistedCalibrationSelectedByCompanion = this.onClickedAssist;
        SignalR.onAssistedTargetDisplaySelectedByCompanion = this.onClickedAssistTargetDisplay;
        SignalR.onSelfCalibrationSelectedByCompanion = this.onClickedSelf;
        document.addEventListener("contextmenu", (ev: MouseEvent): void => {
            ev.preventDefault();
        });
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onVehicleSelectionByTechnician = null;
        SignalR.onAssistedCalibrationSelectedByCompanion = null;
        SignalR.onAssistedTargetDisplaySelectedByCompanion = null;
        SignalR.onSelfCalibrationSelectedByCompanion = null;
    },
    methods: {
        getDeviceStatus(): DeviceStatus {
            return SignalR.getDeviceStatus();
        },
        isValidDevice(): boolean {
            return this.getDeviceStatus() === DeviceStatus.Valid && !this.isPackageTooNew();
        },
        isInvalidDevice(): boolean {
            return this.getDeviceStatus() === DeviceStatus.Invalid && !this.isPackageTooNew();
        },
        isUnknownDevice(): boolean {
            return this.getDeviceStatus() === DeviceStatus.Unknown && !this.isPackageTooNew();
        },
        isDemo(): boolean {
            return Global.IsDemo();
        },
        isPackageTooNew(): boolean {
            return Global.IsVersionBeyondLastSupported();
        },
        packageTooNewText(): string {
            // The device version is too high and not yet supported by the device web
            if (!AUGGIEAVAILABLE) return "";
            return `Device client version ${Auggie.System.Version} is not yet supported by this environment (${Auggie.System.GetEnvironment()}).`;
        },
        packageLatestText(): string {
            return `Latest supported device client version is ${import.meta.env.VITE_LASTSUPPORTEDDEVICEVERSION}`;
        },
        getVersion(): string {
            let version = "n/a";
            if (AUGGIEAVAILABLE)
                version = Auggie.System.Version;
            return `Auggie ${import.meta.env.VITE_VERSION} (${version})`;
        },
        getEnvironment(): string {
            if (Global.IsDemo()) return " - DEMO";
            if (Global.Environment === "PRD") return "";
            return ` - ${Global.Environment}`;
        },
        onClickedAssist(): void {
            this.loading = true;
            Global.SessionType = SessionType.AssistedCalibration;
            CalibrationEventSupport.sendEvent(CalibrationEvent.AssistedCalibrationSelected);
            setTimeout((): void => {// allow page update in case it takes longer
                this.$router.push("/Calibration");
            }, 100);
        },
        onClickedAssistTargetDisplay(): void {
            this.loading = true;
            Global.SessionType = SessionType.AssistedTargetDisplay;
            CalibrationEventSupport.sendEvent(CalibrationEvent.AssistedTargetDisplaySelected);
            setTimeout((): void => {// allow page update in case it takes longer
                this.$router.push("/Calibration");
            }, 100);
        },
        onClickedSelf(): void {
            this.loading = true;
            Global.SessionType = SessionType.SelfCalibration;
            CalibrationEventSupport.sendEvent(CalibrationEvent.SelfCalibrationSelected);
            setTimeout((): void => {// allow page update in case it takes longer
                this.$router.push("/Calibration");
            }, 100);
        },
        setVehicleSelectionByTech(year: number, make: string, model: string, type: CalibrationType): void {
            Global.IsTechControlled = true;
            if (Global.SessionType === SessionType.Unknown)
                Global.SessionType = SessionType.AssistedCalibration;
            Global.Make = make;
            Global.Model = model;
            Global.Year = year;
            Global.Calibration = type;
            setTimeout((): void => {// allow page update in case it takes longer
                this.$router.push("/Calibration");
            }, 100);
        },
        startConnection(): void {
            if (Global.DetailLog) console.log(`Entry: startConnection - device status ${this.getDeviceStatus()}`);
            this.loading = false;
            if (this.isInvalidDevice() || this.isPackageTooNew()) return;// this device can't do calibrations
            this.loading = true;
            if (this.isUnknownDevice()) {
                if (Global.DetailLog) console.log("Unknown device in startConnection");
                // we don't have a device yet (device session not yet started through SignalR)
                if (this.MaxStartSessionAttemps > 0) {
                    // start a device session
                    if (Global.DetailLog) console.log("sending deviceStartSession");
                    if (SignalR.isConnected) {
                        // start a device session, count as an attempt
                        if (Global.DetailLog) console.log("startConnection, sending deviceStartSession");
                        SignalR.sendDeviceStartSession();
                        this.MaxStartSessionAttemps--;
                    } else {
                        // we're still? waiting for SignalR, so this doesn't count as a failed attempt
                        if (Global.DetailLog) console.log("startConnection, waiting for SignalR");
                    }
                    setTimeout((): void => {
                        this.startConnection(); // try to start the session again
                    }, this.StartSessionInterval);
                } else {
                    this.loading = false;
                    if (Global.DetailLog) console.log("startConnection, unable to get response to deviceStartSession");
                    confirmOk("Unable to start a session for this device. Possible server connectivity problems or weak WiFi signal.")
                        .then((clicked: ButtonClicked): void => {
                            window.location.href = "/";// start from scratch by reloading the page
                        });
                }
                return;
            }
            this.loading = false;
            if (Global.DetailLog) console.log("startConnection OK");

            if (!AUGGIEAVAILABLE) {
                this.startSession();
                return;
            }

            Global.MainApp.clearSession();

            if (!Global.IsPrototype() && !Global.IsDemo()) {

                this.loading = true;

                let deviceGuid: string | null = null;
                let osDescription: string | null = null;
                let osArchitecture: string | null = null;
                if (Global.IsVersionAtLeast("2024.101.1")) {
                    deviceGuid = Global.DeviceGuid;
                    osDescription = Auggie.System.OsDescription;
                    osArchitecture = Auggie.System.OsArchitecture;
                }
                const dr = new DataRequest();
                dr.autoToastOnFailure = false;
                dr.$get<PackageDto>("/Service/Packages/PackageInformation", {
                    Version: Auggie.System.Version,
                    DeviceGuid: deviceGuid,
                    OsDescription: osDescription,
                    osArchitecture: osArchitecture
                }).then((auggiePackage: PackageDto): void => {
                    this.loading = false;
                    if (auggiePackage.PackageId) {
                        // There is a new package
                        if (Global.DetailLog) console.log(`Package ${auggiePackage.PackageName} available - ${JSON.stringify(auggiePackage)}`);
                        Global.PackageUpgrade = auggiePackage;
                        const url = Utility.formatUrl(`/Service/Packages/GetPackage/${Global.PackageUpgrade!.PackageName}`, {}, Global.GetWebAPIUrl());
                        const err = Auggie.PackageDownload.Start(url,
                            Global.PackageUpgrade!.PackageName,
                            Global.PackageUpgrade!.Size, Global.PackageUpgrade!.Hash,
                            `${import.meta.env.VITE_DEVICE_SERIAL_NUMBER_HEADER!}: ${Global.DeviceSerialNumber}`,
                            `Authorization: Bearer ${Auggie.System.GetToken()}`);
                        if (auggiePackage.RequiredInstall || (auggiePackage.InstallBy && new Date(auggiePackage.InstallBy) < new Date())) {
                            // required install
                            if (!err)
                                this.$router.push("/Upgrade/Required");
                            else
                                this.$router.push("/Upgrade/Failed");
                        } else if (Auggie.PackageDownload.IsDone()) {
                            // background download was started/resumed
                            confirmYesNo("An upgrade is available for this device. Do you want to install it now?")
                                .then((clicked: ButtonClicked): void => {
                                    if (clicked === ButtonClicked.Ok) {
                                        this.$router.push("/Upgrade/Installing");
                                        return;
                                    }
                                    this.startSession();
                                });
                        } else {
                            this.startSession();
                        }
                    } else {
                        this.startSession();
                    }
                }).catch((reason: any): void => {
                    this.loading = false;
                    this.startSession();
                });
            } else {
                this.startSession();
            }
        },
        startSession(): void {

            if (Global.IsDemo()) {
                Global.MainApp.setDemoSession();
                this.url = Utility.formatUrl("/not a real url", {});
                return;
            }

            this.loading = true;

            Global.MainApp.clearSession();

            let packageVersion = "";
            if (AUGGIEAVAILABLE)
                packageVersion = Auggie.System.Version;

            const dr = new DataRequest();
            dr.autoToastOnFailure = false;
            const calibrationSession = {
                DeviceGuid: Global.DeviceGuid,
                PackageVersion: packageVersion,
                SiteVersion: import.meta.env.VITE_VERSION || "",
            } as CalibrationSessionDto;
            dr.$post<CalibrationSessionDto, CalibrationSessionDto>("/Service/CalibrationSession", null, calibrationSession)
                .then((session: CalibrationSessionDto): void => {
                    this.loading = false;
                    if (session.UpdateResult.Success) {
                        Global.MainApp.setSession(session);
                        this.url = Utility.formatUrl("/", { c: Global.CalibrationSession!.CompanionGuid, s: Global.CalibrationSession!.CalibrationSessionGuid }, Global.GetCompanionUrl());
                        CalibrationEventSupport.sendBatteryInformation();// send initial battery info
                        CalibrationEventSupport.sendTemperatureInformation();
                        CalibrationEventSupport.sendCpuInformation();
                        if (AUGGIEAVAILABLE) {
                            if (Global.IsVersionAtLeast("2024.101.1")) {
                                const urlChunked = Utility.formatUrl("/Service/ChunkedUpload", {}, Global.GetWebAPIUrl());// chunked uploads in background (WINDOWS)
                                const url = Utility.formatUrl("/Service/Uploads/UploadLog", {}, Global.GetWebAPIUrl());// uploads in background (LINUX)
                                Auggie.System.UploadLogsChunked(urlChunked, url, Global.DeviceName, Global.CalibrationSession!.CalibrationSessionGuid,
                                    `${import.meta.env.VITE_DEVICE_SERIAL_NUMBER_HEADER!}: ${Global.DeviceSerialNumber}`, `Authorization: Bearer ${Auggie.System.GetToken()}`);
                            } else if (Global.IsVersionAtLeast("2023.019.12")) {
                                const url = Utility.formatUrl("/Service/Uploads/UploadLog", {}, Global.GetWebAPIUrl());
                                Auggie.System.UploadLogs(url, Global.DeviceName, `${import.meta.env.VITE_DEVICE_SERIAL_NUMBER_HEADER!}: ${Global.DeviceSerialNumber}`, `Authorization: Bearer ${Auggie.System.GetToken()}`);
                            }
                        }
                    } else {
                        confirmOk(session.UpdateResult.Message || "The server was unable to establish a session")
                            .then((clicked: ButtonClicked): void => {
                                this.startConnection();// try again
                            });
                    }
                })
                .catch((reason: any): void => {
                    this.loading = false;
                    confirmOk("Unable to establish a session for this device. Possible server connectivity problems or weak WiFi signal.")
                        .then((clicked: ButtonClicked): void => {
                            this.startConnection();// try again
                        });
                });
        },
        killTeamViewer(): void {
            // Shut down TeamViewer
            if (AUGGIEAVAILABLE && Global.IsVersionAtLeast("2022.286.2")) {
                Auggie.TeamViewer.Off();
            }
        },
        updateDeviceInfo(): void {
            this.$forceUpdate();
        },
        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({ Page: "Home" } as DeviceStatusDto);
        },
    },
});
</script>
